import {
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
  type RowClickActions,
} from '@blaze-shared/elements-domain';
import { type Workflow } from '@blaze-shared/workflow-domain';

import { type BulkAction, type RowAction } from '../models/actions';
import { type ComponentWorkflows } from '../models/ai.model';
import {
  getActionWorkflowContext,
  type IndexedActionGroup,
  type RecordActionGroup,
  type ScalarActionGroup,
  warnMissingComponentWorkflow,
} from '../page/component-workflow-key';
import { buildCustomActionModel } from '../page/prop-builders';

import { type RecordClickIntent, toEngineRecordClickAction } from './record-actions.appearance';
import { STANDARD_BULK_ACTIONS_MAP, STANDARD_RECORD_ACTIONS_MAP } from './record-actions.standard';

export interface ResolvedRecordActions<TModel> {
  props: TModel[];
  workflows: Workflow[];
}

export interface ResolvedRecordClick {
  props?: { enabled: boolean; action: RowClickActions };
  workflow?: Workflow;
}

export function resolveRecordActions(
  elementId: string,
  actions: RowAction[] | undefined,
  componentWorkflows: ComponentWorkflows,
  group: RecordActionGroup,
): ResolvedRecordActions<PropertiesConfigInputContainerItemUnifiedRowActionModel> {
  return resolveActionList(
    elementId,
    actions,
    componentWorkflows,
    STANDARD_RECORD_ACTIONS_MAP,
    group,
  );
}

export function resolveBulkActions(
  elementId: string,
  actions: BulkAction[] | undefined,
  componentWorkflows: ComponentWorkflows,
): ResolvedRecordActions<PropertiesConfigInputContainerItemUnifiedBulkActionModel> {
  return resolveActionList(
    elementId,
    actions,
    componentWorkflows,
    STANDARD_BULK_ACTIONS_MAP,
    'BulkActions',
  );
}

function resolveActionList<TModel>(
  elementId: string,
  actions: (RowAction | BulkAction)[] | undefined,
  componentWorkflows: ComponentWorkflows,
  standardMap: ReadonlyMap<string, TModel>,
  group: IndexedActionGroup,
): ResolvedRecordActions<TModel> {
  const props: TModel[] = [];
  const workflows: Workflow[] = [];

  if (!Array.isArray(actions)) {
    return { props, workflows };
  }

  actions.forEach((action, index) => {
    if (action.$type === 'custom') {
      const workflowContext = getActionWorkflowContext(elementId, componentWorkflows, group, index);

      props.push(buildCustomActionModel(action, workflowContext) as TModel);

      if (workflowContext) {
        workflows.push(workflowContext.workflow);
      } else {
        warnMissingComponentWorkflow(elementId, componentWorkflows, group, index);
      }

      return;
    }

    const standardAction = standardMap.get(action.$type);

    if (standardAction) {
      props.push({ ...standardAction, aiDescription: action.aiDescription });
    }
  });

  return { props, workflows };
}

export function resolveRecordClick(
  elementId: string,
  recordClick: RecordClickIntent | undefined,
  componentWorkflows: ComponentWorkflows,
  group: ScalarActionGroup,
): ResolvedRecordClick {
  if (!recordClick) {
    return {};
  }

  if (recordClick.$type !== 'custom') {
    return { props: { enabled: true, action: toEngineRecordClickAction(recordClick) } };
  }

  const workflowContext = getActionWorkflowContext(elementId, componentWorkflows, group);

  if (!workflowContext) {
    warnMissingComponentWorkflow(elementId, componentWorkflows, group);
    return {};
  }

  return {
    props: { enabled: true, action: toEngineRecordClickAction(recordClick) },
    workflow: workflowContext.workflow,
  };
}
